import React, { useState } from 'react';
import { object, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { generateReferralCode, buildReferralUrl } from './referralUtils';
import css from './ReferralShare.module.css';

/**
 * ReferralShare — shows a referral link for the current user.
 *
 * Usage (in ProfileSettingsPage or AccountSettingsPage):
 *   <ReferralShare currentUser={currentUser} />
 *
 * The referral code is derived from the user's UUID.
 * When someone signs up via the link, their protectedData.referredBy
 * is set to the referrer's code.
 */
const ReferralShare = ({ currentUser, className }) => {
  const [copied, setCopied] = useState(false);

  const userId = currentUser?.id?.uuid;
  if (!userId) return null;

  const code = generateReferralCode(userId);
  const baseUrl = process.env.REACT_APP_MARKETPLACE_ROOT_URL || window.location.origin;
  const referralUrl = buildReferralUrl(baseUrl, code);

  const handleCopy = () => {
    navigator.clipboard.writeText(referralUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className={classNames(css.root, className)}>
      <h4 className={css.title}>
        <FormattedMessage id="ReferralShare.title" />
      </h4>
      <p className={css.description}>
        <FormattedMessage id="ReferralShare.description" />
      </p>
      <div className={css.linkRow}>
        <input
          type="text"
          readOnly
          value={referralUrl}
          className={css.input}
          onClick={e => e.target.select()}
        />
        <button type="button" onClick={handleCopy} className={css.copyBtn}>
          {copied
            ? <FormattedMessage id="ReferralShare.copied" />
            : <FormattedMessage id="ReferralShare.copy" />
          }
        </button>
      </div>
      <p className={css.code}>
        <FormattedMessage id="ReferralShare.code" values={{ code }} />
      </p>
    </div>
  );
};

ReferralShare.defaultProps = { className: null, currentUser: null };
ReferralShare.propTypes = { currentUser: object, className: string };

export default ReferralShare;
